"use client";

import { AccountModalTrigger } from "@/features/account/AccountModal";
import { Link } from "@/i18n/Link";
import { clsx } from "@frend-digital/ui";
import { Fragment, useEffect, useState } from "react";
import styles from "./Snackbar.module.css";

const VISIBLE_DURATION_MS = 5000;
const FADE_DURATION_MS = 300;

export type SnackbarMessage = {
	text: string;
	href?: string;
};

const SnackbarMessageLink = ({
	message,
	decorative,
}: { message: SnackbarMessage; decorative?: boolean }) => {
	const content = <span className={styles.messageText}>{message.text}</span>;

	if (message.href) {
		return (
			<Link
				href={message.href}
				className={styles.messageLink}
				aria-hidden={decorative}
				tabIndex={decorative ? -1 : undefined}
			>
				{content}
			</Link>
		);
	}

	return (
		<AccountModalTrigger mode="login" asChild>
			<button
				type="button"
				className={styles.messageLink}
				aria-hidden={decorative}
				tabIndex={decorative ? -1 : undefined}
			>
				{content}
			</button>
		</AccountModalTrigger>
	);
};

// One lap of the ticker: every message, clickable. `decorative` renders a
// visually-identical but non-interactive/hidden copy so the looping second lap
// doesn't duplicate real links for screen readers or the tab order.
const SnackbarTickerLap = ({
	messages,
	decorative,
}: { messages: SnackbarMessage[]; decorative?: boolean }) => (
	<div className={styles.tickerGroup} aria-hidden={decorative}>
		{messages.map((message) => (
			<Fragment key={message.text}>
				<SnackbarMessageLink message={message} decorative={decorative} />
			</Fragment>
		))}
	</div>
);

export const RotatingSnackbarMessage = ({ messages }: { messages: SnackbarMessage[] }) => {
	const [index, setIndex] = useState(0);
	const [visible, setVisible] = useState(true);
	const isMultiMessage = messages.length > 1;

	useEffect(() => {
		if (!isMultiMessage) return;
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

		const timer = setTimeout(
			() => {
				if (visible) {
					setVisible(false);
				} else {
					setIndex((current) => (current + 1) % messages.length);
					setVisible(true);
				}
			},
			visible ? VISIBLE_DURATION_MS : FADE_DURATION_MS,
		);

		return () => clearTimeout(timer);
	}, [visible, isMultiMessage, messages.length]);

	const message = messages[index];

	if (!message) return null;

	return (
		<div className={styles.messageViewport}>
			<div
				className={clsx(
					styles.messageFade,
					visible && styles.messageFadeVisible,
					// On mobile, multi-message snackbars use the continuous ticker below instead
					// of the fade-cycle; a single message still shows here at every breakpoint.
					isMultiMessage && styles.messageFadeDesktopOnly,
				)}
				style={{ transitionDuration: `${FADE_DURATION_MS}ms` }}
			>
				<SnackbarMessageLink message={message} />
			</div>

			{isMultiMessage && (
				<div className={styles.ticker}>
					<div className={styles.tickerTrack}>
						<SnackbarTickerLap messages={messages} />
						<SnackbarTickerLap messages={messages} decorative />
					</div>
				</div>
			)}
		</div>
	);
};
